import { cloneElement, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { getVisibleAdminRoutes } from '@/components/layout/nav/adminRoutes';
import { BottomSheet } from '@/components/ri/BottomSheet';
import { CardList, CardListRow } from '@/components/ri/CardList';
import { Avatar } from '@/components/ri/Avatar';
import { I, type IconProps } from '@/components/ri/icons';
import { useAuth } from '@/hooks/useAuth';

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev';

export interface MobileMoreSheetProps {
  open: boolean;
  onClose: () => void;
  showEvents?: boolean;
}

export function MobileMoreSheet({ open, onClose, showEvents = false }: MobileMoreSheetProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const name = user?.displayName ?? 'User';
  const email = user?.email ?? '';
  const role = user?.role ?? 'Rep';
  const photoUrl = user?.photoUrl ?? null;
  const adminRoutes = getVisibleAdminRoutes(user);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingBottom: 12,
          marginBottom: 14,
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Avatar name={name} src={photoUrl} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>{name}</div>
          <div className="ri-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 1 }}>
            {email} · {role}
          </div>
        </div>
      </div>

      <div className="ri-eyebrow" style={{ marginBottom: 8 }}>Resources</div>
      <CardList>
        <CardListRow onClick={() => go('/how-to')} primary="How to" secondary="Quote decisions, customer views and recovery" dense />
        <CardListRow onClick={() => go('/whats-new')} primary="What’s new" secondary="New features, improvements and fixes" dense />
      </CardList>
      <div style={{ height: 14 }} />

      {showEvents && (
        <CardList>
          <CardListRow onClick={() => go('/events')} primary="Events" secondary="Scan, connect, register, and check in" dense />
        </CardList>
      )}

      <button
        onClick={() => go('/settings')}
        style={{
          width: '100%',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minHeight: 56,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'var(--surface-sunken)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          {cloneElement(I.cog as ReactElement<IconProps>, { size: 17 })}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Settings</div>
          <div className="ri-small" style={{ fontSize: 11.5, marginTop: 2 }}>
            Notifications, profile, preferences
          </div>
        </div>
        <span style={{ color: 'var(--text-tertiary)' }}>
          {cloneElement(I.chevR as ReactElement<IconProps>, { size: 18 })}
        </span>
      </button>

      {adminRoutes.length > 0 && (
        <>
          <div
            style={{
              marginTop: 18,
              marginBottom: 8,
              padding: '0 4px',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
            }}
          >
            <div className="ri-eyebrow">Admin</div>
            <span className="ri-mono" style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
              {role === 'Admin' ? 'Admin only' : `${role} access`}
            </span>
          </div>

          <CardList>
            {adminRoutes.map((r) => (
              <CardListRow
                key={r.k}
                onClick={() => go(r.path)}
                leading={
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: 'var(--surface-sunken)',
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {cloneElement(r.icon as ReactElement<IconProps>, { size: 17 })}
                  </div>
                }
                primary={r.label}
                secondary={r.path}
                trailing={
                  <span style={{ color: 'var(--text-tertiary)' }}>
                    {cloneElement(I.chevR as ReactElement<IconProps>, { size: 18 })}
                  </span>
                }
                dense
              />
            ))}
          </CardList>
        </>
      )}

      <div style={{ marginTop: 18 }}>
        <button
          onClick={() => {
            onClose();
            logout();
          }}
          style={{
            width: '100%',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'center',
            minHeight: 52,
            cursor: 'pointer',
            color: 'var(--danger)',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {cloneElement(I.arrow as ReactElement<IconProps>, { size: 16 })} Sign out
        </button>
      </div>

      <div
        className="ri-small"
        style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)' }}
      >
        Meridian · {APP_VERSION} · {name}
      </div>
    </BottomSheet>
  );
}
